import { useInfiniteQuery } from "@tanstack/react-query";
import type { Environment } from "../../../types";
import type { TokenHistoryItem, TokenHistoryResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_HISTORY_QUERY_KEY = "token-history";

/** 커서 페이지네이션 키. 직전 페이지 마지막 항목에서 파생된다. */
interface HistoryCursor {
  last_timestamp: number;
  last_log_index: number;
  last_tx_index: number;
}

/**
 * 특정 토큰의 거래 이력을 `/v1/public/token/historical`에서 무한스크롤로 조회.
 * dapp-ui가 이미 사용하는 walletServer 호스트를 그대로 쓴다.
 *
 * 페이지네이션: 응답이 빈 배열이면 다음 페이지 없음으로 판단한다. 마지막
 * 부분 페이지(항목 존재) 뒤에 빈 페이지 요청이 한 번 더 발생할 수 있으나,
 * 그 시점에 `hasNextPage`가 false로 확정된다.
 */
export function useTokenHistory(
  env: Environment,
  chainId: number,
  token: string,
  wallet: string,
  enabled = true,
) {
  return useInfiniteQuery<TokenHistoryItem[], Error>({
    queryKey: [TOKEN_HISTORY_QUERY_KEY, env, chainId, token.toLowerCase(), wallet.toLowerCase()],
    initialPageParam: undefined,
    queryFn: async ({ pageParam, signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const params = new URLSearchParams({
        chain_id: String(chainId),
        token,
        wallet,
      });
      const cursor = pageParam as HistoryCursor | undefined;
      if (cursor) {
        params.set("last_timestamp", String(cursor.last_timestamp));
        params.set("last_log_index", String(cursor.last_log_index));
        params.set("last_tx_index", String(cursor.last_tx_index));
      }
      const res = await fetch(
        `${baseUrl}/v1/public/token/historical?${params}`,
        { cache: "no-store", signal },
      );
      if (!res.ok) {
        throw new Error(`Failed to fetch token history: ${res.status}`);
      }
      const json: TokenHistoryResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`Token history API error: ${json.code}`);
      }
      return json.data ?? [];
    },
    getNextPageParam: (lastPage): HistoryCursor | undefined => {
      if (!lastPage || lastPage.length === 0) return undefined;
      const last = lastPage[lastPage.length - 1];
      return {
        last_timestamp: last.timestamp,
        last_log_index: last.log_index,
        last_tx_index: last.tx_index,
      };
    },
    enabled: enabled && !!wallet && !!token && chainId > 0,
    staleTime: 10_000,
  });
}
